/* eslint-disable camelcase */
import { useCallback, useEffect, useRef, useState } from 'react';
import useCountdown from './useCountdown';
import { requestAdminMfaChallenge, verifyAdminMfaCode } from '~/api/adminMfa';
import { isApiErrorBody } from '~/api/api';
import { setAdminMfaChallenge, setAdminMfaSession } from '~/redux/modules/adminMfa';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { applyAdminMfaToken } from './useAdminSessionActions';

const CODE_LENGTH = 6;

/**
 * Requesting, resending and verifying the admin code.
 *
 * Two independent clocks, both live, both seeded from the server and never
 * started locally:
 *
 * - the code's own lifetime, ten minutes, after which the code in the inbox is
 *   no longer accepted;
 * - the resend cooldown, one minute, before another code may be asked for.
 *
 * They must not be conflated. Reusing useOtpTimer for the cooldown would be
 * wrong twice over: its MAX_TIME is 600 — the code lifetime, not a cooldown —
 * so Resend would sit disabled for the code's entire life, which is the exact
 * situation the button exists for.
 *
 * The mount request carries no resend flag, so when a code is already
 * outstanding the server reports that challenge and sends nothing. What comes
 * back is its real remaining life, which is what makes the numbers correct
 * rather than merely present: moving between admin screens remounts this and
 * the countdowns pick up the outstanding challenge where it actually is, no
 * matter how long ago the code was sent. A reload does the same.
 *
 * `isShow` is the modal's own visibility, and the hook needs it for one
 * decision only: whether lifting a lockout should ask for a code straight away
 * or wait for the member to come back. Hidden is not unmounted here -- the gate
 * keeps this alive behind a Verify me button precisely so an outstanding
 * challenge survives being dismissed -- so without it a block that lapses on a
 * screen nobody is looking at would email a code and start its ten minutes
 * running against a member who is not there to read it.
 *
 * @param {Boolean} [isShow] whether the modal is currently on screen
 * @returns {Object} code state, both countdowns, and the request/verify callbacks
 */
const useAdminMfaChallenge = (isShow = true) => {
    const dispatch = useAppDispatch();

    const { id } = useAppSelector(({ profile }) => profile);
    const { maskedEmail, devCode, blockedUntilMs } = useAppSelector(({ adminMfa }) => adminMfa);

    const [code, setCode] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [resendMessage, setResendMessage] = useState('');
    const [isVerifying, setIsVerifying] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [attemptsRemaining, setAttemptsRemaining] = useState<number | undefined>(undefined);
    const [resendsRemaining, setResendsRemaining] = useState<number | undefined>(undefined);
    // Two different blocks, deliberately not one flag.
    //
    // A resend block means no *new* code may be asked for; the code already in
    // the member's inbox is still perfectly valid and they must still be able
    // to type it. A verify block means the opposite -- no code can be checked
    // at all, so the inputs come off the screen. Conflating them showed
    // "You've asked for too many codes" to someone who had asked for one and
    // mistyped it three times, and left the inputs live under a lockout that
    // would refuse anything typed into them.
    const [isResendBlocked, setIsResendBlocked] = useState(false);
    const [isVerifyBlocked, setIsVerifyBlocked] = useState(false);
    const [hasLiveCode, setHasLiveCode] = useState(false);

    const hasRequested = useRef(false);
    // A block that lapsed while the modal was dismissed. The code it owes is
    // sent when the member reopens, not before. See the effects below.
    const needsCodeOnOpen = useRef(false);
    // Whether the lockout countdown was actually started, as opposed to the
    // lockout merely being flagged. See onBlock.
    const hasBlockClockRef = useRef(false);

    // Destructured because the hook's return object is new on every tick: taking
    // a dependency on it would rebuild onRequestCode and onVerify once a second,
    // and with them every effect that depends on those. onStart and onStop are
    // stable; only `seconds` changes.
    const { seconds: cooldownSeconds, onStart: onStartCooldown } = useCountdown();
    const {
        seconds: codeExpiresInSeconds,
        onStart: onStartCodeLife,
        onStop: onStopCodeLife
    } = useCountdown();

    // The lockout's own clock. Separate from the resend cooldown because the
    // two run at once -- a locked-out member is also inside a resend cooldown --
    // and the modal shows this one while hiding that one.
    const {
        seconds: blockedSeconds,
        onStart: onStartBlocked,
        onStop: onStopBlocked
    } = useCountdown();

    // Entering the lockout, from wherever it is reported: the 429 the verify
    // call answers with, the same 429 from asking for a code, or the
    // outstanding block the challenge reports on load. All three have to land
    // in the same view -- clicking Verify into a lockout should look exactly
    // like reloading into one.
    const onBlock = useCallback((seconds?: number, isFromStore = false) => {
        setIsVerifyBlocked(true);

        // The code's own clock stops here, and this is the whole of the fix for
        // two timers running at once. A locked-out member cannot have the code
        // checked, so counting down what is left of its ten minutes is counting
        // down something that has already stopped mattering -- and the block
        // outlasts the code several times over, so it always reached zero
        // first. Only the lockout runs while the lockout is in force.
        //
        // Started at zero rather than stopped. onStop leaves `seconds` at
        // whatever it last read, and a stopped clock still has its number on
        // screen: the label went on saying "Expires in 7:12" through the block
        // and was still saying it afterwards, which is the reduced expiry --
        // not a short code, a frozen one. Zero renders as nothing at all.
        onStartCodeLife(0);
        setHasLiveCode(false);

        // Only arm the clock when the server actually gave one. Starting it
        // with nothing leaves it at zero, which the effect below would read as
        // "the block has finished" and undo the lock a frame after setting it.
        if ((seconds || 0) > 0) {
            hasBlockClockRef.current = true;

            onStartBlocked(seconds);

            // Recorded where it outlives this hook. Seeding from the store is
            // not news, so it does not write back.
            if (!isFromStore) {
                dispatch(setAdminMfaChallenge({
                    blockedUntilMs: Date.now() + (seconds as number) * 1000
                }));
            }
        }
    }, [dispatch, onStartBlocked, onStartCodeLife]);

    // Seed from the store, before any request answers.
    //
    // This is what makes a reload behave like the click that caused the
    // lockout. The server reports the block once, and whether the modal is
    // mounted to receive it is a timing accident; the deadline in the store is
    // not, so the locked view is up from the first render.
    useEffect(() => {
        if (!blockedUntilMs || isVerifyBlocked) return;

        const remaining = Math.ceil((blockedUntilMs - Date.now()) / 1000);

        if (remaining > 0) {
            onBlock(remaining, true);

            return;
        }

        // Lapsed while nothing was mounted to watch it -- the member left the
        // restricted area during the block and came back after it. Nothing is
        // owed: no code was sent in their absence and none is owed for the time
        // they were away, and the ordinary mount request below covers the entry
        // they have just made. Only the dead deadline needs clearing, so
        // nothing later reads it as a member still locked out.
        dispatch(setAdminMfaChallenge({ blockedUntilMs: undefined }));
    }, [blockedUntilMs, dispatch, isVerifyBlocked, onBlock]);

    const onChange = useCallback((value: string) => {
        setCode(value);
        setErrorMessage('');
    }, []);

    const onRequestCode = useCallback(async (isResend = false) => {
        if (!id) return;

        setIsSending(true);
        // Kept separate from errorMessage: OTPInput's `message` clears on the
        // next keystroke, so a delivery failure shown there would vanish the
        // moment the member starts typing.
        setResendMessage('');

        // Drop the previous dev code before asking for another one.
        //
        // It lives in redux and is only replaced when the response lands, so
        // leaving it in place means the modal goes on showing the *old* code
        // for the whole round trip. A resend mints a new code and invalidates
        // that one, and the number on screen is the obvious thing to copy --
        // so it gets typed, rejected, and counts against the three attempts.
        //
        // Clearing here rather than gating the display on isSending also means
        // a request that fails cannot leave a dead code on screen.
        dispatch(setAdminMfaChallenge({ devCode: undefined }));

        try {
            const response = await requestAdminMfaChallenge(id, isResend);

            dispatch(setAdminMfaChallenge({
                maskedEmail: response?.masked_email,
                nextAllowedAt: response?.next_allowed_at,
                codeExpiresAt: response?.expires_at,
                // Only ever present from a local backend with the dev bypass on.
                devCode: response?.dev_code
            }));

            // From the server's own measurement, so an outstanding challenge
            // resumes at its real remaining life rather than restarting at ten
            // minutes.
            onStartCooldown(response?.resend_in_seconds);

            // A blocked member gets no code clock. The backend refuses the
            // request outright while a lockout is in force, so this branch is
            // belt to that braces -- but the ordering is the point either way:
            // start the lifetime only when there is a usable code to time.
            if ((response?.blocked_in_seconds || 0) > 0) {
                onBlock(response?.blocked_in_seconds);
            } else {
                onStartCodeLife(response?.expires_in_seconds);
                setHasLiveCode((response?.expires_in_seconds || 0) > 0);
            }

            setIsResendBlocked(false);

            // A new code resets the attempt budget server-side, so carrying the
            // old counts over would show "1 attempt left." against a code that
            // has three. The rejection message goes with them — it was about a
            // code that no longer exists.
            setAttemptsRemaining(undefined);
            setErrorMessage('');

            setResendsRemaining(response?.resends_remaining);
        } catch (error) {
            if (isApiErrorBody(error)) {
                onStartCooldown(error.retry_after_seconds);

                setResendsRemaining(error.resends_remaining);
                setIsResendBlocked(error.error_code === 'mfa_resends_exhausted');

                // Asking for a code while locked out is refused with the same
                // error the verify path returns, because it is the same
                // lockout -- a new code is not a way round it.
                if (error.error_code === 'mfa_attempts_exhausted') {
                    onBlock(error.retry_after_seconds);
                }

                setResendMessage(error.message || "We couldn't send a code just now. Try again in a moment.");
            } else {
                setResendMessage("We couldn't send a code just now. Try again in a moment.");
            }
        } finally {
            setIsSending(false);
        }
    }, [dispatch, id, onBlock, onStartCodeLife, onStartCooldown]);

    const onVerify = useCallback(async () => {
        if (!id || code.length !== CODE_LENGTH) return;

        setIsVerifying(true);

        try {
            const response = await verifyAdminMfaCode(id, code);
            const mfa = response?.mfa || {};

            applyAdminMfaToken(mfa);
            dispatch(setAdminMfaSession(mfa));

            // The code is spent. Stop counting rather than let a dead clock run
            // out behind the closing modal.
            onStopCodeLife();
            setHasLiveCode(false);

            return true;
        } catch (error) {
            setCode('');

            if (isApiErrorBody(error)) {
                setErrorMessage(error.message || "That code didn't work. Check the email and try again.");
                setAttemptsRemaining(error.attempts_remaining);

                if (error.error_code === 'mfa_attempts_exhausted') {
                    // Both clocks: the lockout drives the locked view, the
                    // cooldown keeps Resend disabled underneath it.
                    onBlock(error.retry_after_seconds);
                    onStartCooldown(error.retry_after_seconds);
                }

                // The server rejected it because it had lapsed, whatever the
                // local clock thinks — believe the server and say so.
                if (error.error_code === 'mfa_code_expired') {
                    onStopCodeLife();
                    setHasLiveCode(false);
                }
            } else {
                setErrorMessage("We couldn't check that code. Try again.");
            }

            return false;
        } finally {
            setIsVerifying(false);
        }
    }, [code, dispatch, id, onBlock, onStartCooldown, onStopCodeLife]);

    // The lockout lifting is a state change, not just a zero on a clock: the
    // modal has to put the inputs back. Stopping the countdown keeps it from
    // sitting at 0 and re-firing this on every tick.
    //
    // Guarded on the clock having been armed, so a block reported without a
    // duration stays locked until the server says otherwise, rather than
    // flickering straight back to a form that cannot be used.
    useEffect(() => {
        if (!isVerifyBlocked || !hasBlockClockRef.current || blockedSeconds > 0) return;

        hasBlockClockRef.current = false;

        setIsVerifyBlocked(false);
        onStopBlocked();

        dispatch(setAdminMfaChallenge({ blockedUntilMs: undefined }));

        // Nothing was issued during the block -- the server would not, and the
        // code that earned it expired inside the first ten minutes of the hour
        // -- so lifting the lock leaves the inputs back on screen with nothing
        // to type into them. A code has to be asked for; the only question is
        // when, and that is what the member can see.
        //
        // Marked as the mount request either way: the effect below wakes on
        // the same store change and must not send one of its own.
        hasRequested.current = true;

        if (isShow) {
            // They are watching the lockout run out. Ask now, so the inputs
            // come back with a code already on its way, and so the ten minutes
            // start at the moment it is sent.
            onRequestCode(false);
        } else {
            // Dismissed, and the gate is showing Verify me instead. Sending a
            // code to a screen nobody is on spends its life unread: by the time
            // they press the button the ten minutes can be most of the way
            // gone, and on the resend limit that is a code wasted. Deferred to
            // the click.
            needsCodeOnOpen.current = true;
        }
    }, [blockedSeconds, dispatch, isShow, isVerifyBlocked, onRequestCode, onStopBlocked]);

    // The deferred request, on the way back in.
    //
    // Verify me is what runs this, since reopening is all that button does.
    // The code is minted, sent and timed from here, which is the point -- the
    // member is looking at the modal when its ten minutes start.
    useEffect(() => {
        if (!isShow || !needsCodeOnOpen.current || !id) return;

        needsCodeOnOpen.current = false;
        hasRequested.current = true;

        onRequestCode(false);
    }, [id, isShow, onRequestCode]);

    // One challenge on mount, without the resend flag — so if a code is already
    // outstanding the server just reports it. The ref keeps a re-render from
    // repeating the call; the server-side idempotency is what makes a reload or
    // a second visit harmless.
    useEffect(() => {
        if (hasRequested.current || !id) return;

        // Not while blocked. The request would be refused -- the server neither
        // reports nor mints a code for a locked-out member -- and the deadline
        // /access has already put in the store is enough to raise the locked
        // view. The code is asked for when the block lifts, above.
        if (blockedUntilMs && blockedUntilMs > Date.now()) return;

        hasRequested.current = true;

        onRequestCode(false);
    }, [blockedUntilMs, id, onRequestCode]);

    return {
        code,
        codeLength: CODE_LENGTH,
        maskedEmail,
        devCode,
        errorMessage,
        resendMessage,
        attemptsRemaining,
        resendsRemaining,
        isResendBlocked,
        // Either signal is enough. The flag and the clock are set together, but
        // only one of them has to survive for the member to be shown the
        // lockout rather than a form that cannot be submitted -- and being
        // shown the lockout a moment too long is the harmless direction.
        isVerifyBlocked: isVerifyBlocked || blockedSeconds > 0,
        blockedSeconds,
        isVerifying,
        isSending,
        cooldownSeconds,
        codeExpiresInSeconds,
        // Distinct from "no code yet": a code was issued and has since lapsed,
        // which is worth telling them, whereas the initial state is not.
        isCodeExpired: hasLiveCode && codeExpiresInSeconds <= 0,
        isResendDisabled: isVerifying || isSending || isResendBlocked || isVerifyBlocked || cooldownSeconds > 0,
        onChange,
        onVerify,
        onRequestCode,
        // The Resend affordance. Distinct from the mount call: this one asks for
        // a new code and counts against the limit.
        onResendCode: () => onRequestCode(true)
    };
};

export default useAdminMfaChallenge;
